import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useFetchStore } from '@/stores/fetch'
import type { IClient } from '@/types/Client'
import type { ListResponse } from '@/types/Common'
import type { IInstance } from '@/types/Instance'

/**
 * §9.2's one shared cache: the flat instance + client list the command palette, the breadcrumb
 * and every cross-link read from.
 *
 * # Why this exists at all, and why it is the *only* store of its kind
 *
 * §9.2 names three deliberate patterns instead of a query-cache library (unapproved, and it would
 * fight `createFetch`): page-owned state for ~80 % of screens, `libs/polling.ts` for live data,
 * and this — one store, for the one dataset that is read by components which have no business
 * fetching it themselves. The topbar breadcrumb cannot issue a request per navigation, and the
 * Cmd+K palette exists to answer "an alert says instance X is broken → get to instance X" in one
 * keystroke, which a per-keystroke round trip does not do (§9.1).
 *
 * Everything else stays page-owned. The moment a second store starts caching a list "because it
 * is convenient", the SPA has a query cache with no invalidation strategy.
 *
 * # Freshness
 *
 * TTL 5 min, and **invalidated after any client or instance mutation** — the two rules §9.2
 * states. The TTL is the ceiling for a change made in another tab or by another operator; the
 * invalidation is what makes a rename this operator just performed visible in their own
 * breadcrumb immediately. `remember()` covers the third case: a cold deep-link into
 * `/instances/:id` where the cache has never been filled, so the page's own reads seed it.
 *
 * # What is deliberately not here
 *
 * **No poll.** §9.2: a poll started in a store instead of a component leaks, because there is no
 * effect scope to dispose it. The instance list screen polls; this cache does not.
 *
 * **No secrets.** §9.6 puts the install URL in the bearer-grade class and says outright that it
 * must not land in the cached fleet data. Nothing beyond a name, an id and the server-computed
 * `access_level` is stored, and adding a field here is a security decision, not a convenience.
 */

/** One palette/breadcrumb row. Deliberately the smallest shape that answers "where do I go". */
export interface FleetInstance {
    id:          string
    name:        string
    client_id:   string
    client_name: string

    /** Server-computed (§6.1). Carried so the palette can mark read-only destinations, never recomputed. */
    access_level: string
}

export interface FleetClient {
    id:   string
    name: string
}

/** §9.2's figure, spelled once. */
const TTL_MS = 5 * 60 * 1000

export const useFleetStore = defineStore('fleet', () => {
    const clients   = ref<FleetClient[]>([])
    const instances = ref<FleetInstance[]>([])

    /** Epoch millis of the last successful load; `0` means "never loaded, or invalidated". */
    const loadedAt = ref(0)
    const loading  = ref(false)

    /**
     * The in-flight load, so N components mounting at once produce one pair of requests rather
     * than N. It is a plain module-closure variable rather than a ref: nothing renders it, and a
     * reactive promise would only invite somebody to await it in a template.
     */
    let inFlight: Promise<void> | null = null

    /**
     * A function, deliberately **not** a `computed`. A computed caches against its reactive
     * dependencies, and the only reactive value here is `loadedAt` — `Date.now()` is not one. The
     * computed would therefore evaluate to `true` once and stay `true` forever, and the five-minute
     * TTL would never expire. This is the same shape the app store uses for `isLoggedIn()`.
     */
    const isFresh = (): boolean => loadedAt.value > 0 && Date.now() - loadedAt.value < TTL_MS

    const clientById   = (id: string): FleetClient | undefined => clients.value.find(item => item.id === id)
    const instanceById = (id: string): FleetInstance | undefined => instances.value.find(item => item.id === id)

    const clientNameOf = (id: string): string => clientById(id)?.name ?? ''

    /**
     * Both lists in full, in one pass.
     *
     * `get_all=true` is the house query convention's own escape hatch (§6.1) and is right here for
     * a reason the page-owned lists cannot use: this cache is a *navigation index*, and a paged
     * index that stops at row 20 cannot answer "get me to instance X". Both endpoints are
     * `AccessScope`-filtered server-side, so "all" means "all this operator may see" — the
     * fleet is sized at 20–50 instances with 200 as the headroom figure (§1 decision 1, O-7).
     *
     * A failed load leaves the previous contents and an unchanged `loadedAt`, so a transient
     * error empties nothing and the next caller retries. A 401 has already been turned into a
     * redirect by `stores/fetch.ts` before this sees it.
     */
    const load = async (): Promise<void> => {
        const fetchStore = useFetchStore()

        loading.value = true

        try {
            const [clientResult, instanceResult] = await Promise.all([
                fetchStore.fetch('/clients?get_all=true').get().json<ListResponse<IClient>>(),
                fetchStore.fetch('/instances?get_all=true').get().json<ListResponse<IInstance>>(),
            ])

            if (clientResult.statusCode.value !== 200 || instanceResult.statusCode.value !== 200) return

            const loadedClients: FleetClient[] = (clientResult.data.value?.items ?? [])
                .map(client => ({ id: client.id, name: client.name }))

            const nameOf = new Map(loadedClients.map(client => [client.id, client.name]))

            clients.value   = loadedClients
            instances.value = (instanceResult.data.value?.items ?? []).map(instance => ({
                id:           instance.id,
                name:         instance.name,
                client_id:    instance.client_id,
                client_name:  nameOf.get(instance.client_id) ?? '',
                access_level: instance.access_level,
            }))

            loadedAt.value = Date.now()
        } finally {
            loading.value = false
        }
    }

    /** Loads only when the cache is cold or stale; concurrent callers share the one request. */
    const ensureLoaded = (): Promise<void> => {
        if (isFresh()) return Promise.resolve()
        if (inFlight)  return inFlight

        inFlight = load().finally(() => { inFlight = null })

        return inFlight
    }

    /**
     * Marks the cache stale without emptying it. Called after every client and instance mutation
     * (§9.2), and *not* awaited by the mutating screen — the next navigation reloads, while the
     * stale names stay on screen until then rather than blinking out.
     */
    const invalidate = () => { loadedAt.value = 0 }

    /**
     * Upserts one instance and its client from a detail page's own reads.
     *
     * This is what makes a cold deep-link into `/instances/:id/settings` render "Acme › edge-01 ›
     * Settings" in the breadcrumb on first paint instead of after a full fleet load. It does not
     * touch `loadedAt`: one known row is not a fresh index, and claiming otherwise would suppress
     * the load the palette still needs.
     */
    const remember = (instance: Pick<IInstance, 'id' | 'name' | 'client_id' | 'access_level'>, clientName: string) => {
        const client: FleetClient = { id: instance.client_id, name: clientName }

        const clientIndex = clients.value.findIndex(item => item.id === client.id)
        if (clientName) {
            if (clientIndex === -1) clients.value.push(client)
            else                    clients.value[clientIndex] = client
        }

        const row: FleetInstance = {
            id:           instance.id,
            name:         instance.name,
            client_id:    instance.client_id,
            client_name:  clientName || clientNameOf(instance.client_id),
            access_level: instance.access_level,
        }

        const index = instances.value.findIndex(item => item.id === row.id)
        if (index === -1) instances.value.push(row)
        else              instances.value[index] = row
    }

    return {
        clients, instances, loading, isFresh,
        ensureLoaded, invalidate, remember,
        clientById, instanceById, clientNameOf,
    }
})
